import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import { getFaqs } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const TABS = [
  { value: "all", label: "All" },
  { value: "general", label: "General" },
  { value: "booking", label: "Booking" },
  { value: "pricing", label: "Pricing" },
  { value: "temple", label: "Temple Visit" },
  { value: "whatsapp", label: "WhatsApp Support" },
];

const Faq = () => {
  const [tab, setTab] = useState("all");
  const { data: faqs = [] } = useQuery({ queryKey: ["faqs", "all"], queryFn: () => getFaqs() });
  const filtered = tab === "all" ? faqs : faqs.filter((f) => f.category === tab);
  const faqJsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })),
  }), [faqs]);

  return (
    <Layout>
      <Seo
        title="FAQ | Pooja Booking, Pricing & Temple Visit Questions — Gokarna"
        description="Answers to common questions about booking poojas in Gokarna — pricing, the booking process, temple visits, and WhatsApp support."
        jsonLd={faqJsonLd}
      />
      <section className="bg-secondary text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-16">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-gold">Help Center</span>
          <h1 className="mt-2 font-heading text-4xl sm:text-5xl text-white tracking-tight">Frequently Asked Questions</h1>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 sm:px-8 py-14">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="faq-tabs">
          {TABS.map((t) => (
            <button key={t.value} onClick={() => setTab(t.value)} data-testid={`faq-tab-${t.value}`} className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${tab === t.value ? "bg-primary text-white border-primary" : "bg-white text-secondary border-border hover:border-primary"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {filtered.map((f) => (
            <AccordionItem key={f.id} value={f.id} className="bg-white border border-border rounded-xl px-5">
              <AccordionTrigger className="text-left font-medium text-secondary hover:no-underline">{f.question}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">{f.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </Layout>
  );
};

export default Faq;
